"use client";

import { useEffect, useRef } from "react";
import { useSnapshot } from "valtio";
import { usePathname } from "next/navigation";
import { deviceDetector, getItem, setItem } from "utils";
import log from "logbeacon";
import { rootStore } from "../store";
import {
  MessageType,
  useBridge,
  type DeepLinkOpenedData,
  extractResultId,
  getBridge,
  handleDeepLinkOpened,
} from "../lib/bridge";
import { LOCAL_STORAGE_KEYS, ROUTER_PATH } from "../config";
import { useMemberUser } from "./useMemberUser";
import { useNotificationPermission } from "./useNotificationPermission";
import { useAppPushSubscription } from "./useAppPushSubscription";
import { useChatRetainModal } from "./useChatRetainModal";
import { useIsPc } from "./useIsPc";
import { useShellRouter } from "./useShellAwareRouter";
import { getDeferredDeepLinkPullDelaysMs } from "../lib/bridge/deferredDeepLinkPull";
import { isLoginRoute } from "@/src/hooks/onboarding/isOnboardingRoute";
import { normalizeProductPathname } from "@/src/utils/proxyclass/onboardingStepGateLogic";
import {
  resolvePushNotificationRoute,
  type PushNotificationClickedPayload,
} from "./resolvePushNotificationRoute";
import {
  inferPushEntryDomain,
  setPushEntryMarker,
} from "./push/pushEntryMarker";
import { trackPushNotificationClick } from "./push/trackPushNotificationClick";

export type { PushNotificationClickedPayload };

/**
 * 根据 market/category 解析为路由 path 片段（crypto | us-stocks | hk-stocks）
 * @returns 可用的 path 片段，无法解析时返回 undefined
 */
export function getPath(market: string, category: string): string | undefined {
  if (category === "token" || category === "crypto") {
    return "crypto";
  }
  if (category === "stock") {
    if (market === "US") {
      return "us-stocks";
    }
    if (market === "HK") {
      return "hk-stocks";
    }
  }
  return undefined;
}

export function useInitApp({
  isClientReady = true,
}: {
  isClientReady?: boolean;
} = {}): void {
  const { isLoggedIn, isFetching } = useSnapshot(rootStore.login);
  const { on, isInApp } = useBridge();
  const [isPC] = useIsPc();
  const { data: memberInfo } = useMemberUser();
  const router = useShellRouter();
  const isApp = deviceDetector.isApp();
  const { data: notificationPermission } = useNotificationPermission();
  const { requestOpenRetainModalFromPush, tryAutoOpenRetainModal } =
    useChatRetainModal({ isClientReady });
  const { openAppPush } = useAppPushSubscription();
  const pathname = usePathname();
  const appInteractiveReadySent = useRef(false);
  const productPath = normalizeProductPathname(pathname ?? "");
  const onLoginPage = isLoginRoute(productPath);

  // 首屏可交互后通知 App 隐藏启动屏（/login 由 LoginPage 发 web-login-gate-ready）
  useEffect(() => {
    if (!isApp || appInteractiveReadySent.current) {
      return;
    }
    if (onLoginPage) {
      return;
    }
    if (isFetching) {
      return;
    }

    const timer = setTimeout(() => {
      if (appInteractiveReadySent.current) {
        return;
      }
      if (isFetching) {
        return;
      }
      appInteractiveReadySent.current = true;
      getBridge().sendAppInteractiveReady();
      log.debug("[useInitApp] app-interactive-ready sent (stable)");
    }, 400);

    return () => clearTimeout(timer);
  }, [isApp, isFetching, onLoginPage]);

  // 首次进入 App 且已登录、已授权通知时，自动开启推送订阅（仅一次）
  useEffect(() => {
    if (!isApp) {
      return;
    }
    if (
      !getItem(LOCAL_STORAGE_KEYS.IS_FIRST_LOAD) &&
      isLoggedIn &&
      notificationPermission?.enabled
    ) {
      openAppPush();
      setItem(LOCAL_STORAGE_KEYS.IS_FIRST_LOAD, "true");
    }
  }, [isApp, isLoggedIn, notificationPermission?.enabled, openAppPush]);

  // 登录后尝试一次 auto retain；真正何时允许打开由 retain hook 内部的
  // shouldShowRenew / push 优先级 / RootLayoutClient.isClientReady 一起决定。
  useEffect(() => {
    if (!isLoggedIn || isPC) {
      return;
    }
    tryAutoOpenRetainModal();
  }, [isLoggedIn, tryAutoOpenRetainModal, isPC]);

  // 推送点击路由：与原生约定 type 跳转对应页面
  useEffect(() => {
    log.debug("[useInitApp] useEffect", isInApp, on, router);
    if (!isInApp || !on || !router) {
      return;
    }
    const unsubscribe = on(
      MessageType.PUSH_NOTIFICATION_CLICKED,
      (data: PushNotificationClickedPayload) => {
        const pushType = data?.type;
        log.trace("[GTM_CLICK] push_notification_clicked", {
          pushType,
          data,
        });
        // 处理会员挽留弹窗
        if (pushType?.startsWith("fcm.body.member_retention.push.")) {
          /**
           * push 点击不会直接弹 retain modal，而是先登记一个待处理请求。
           * 真实弹窗时机交给 useChatRetainModal 内部统一处理：
           * - 最多等待 5s 让 ownerInfo / memberInfo hydration 完成
           * - push 与 auto 共用同一个会话级展示锁，避免连续弹两次
           */
          requestOpenRetainModalFromPush();
          return;
        }

        // V3.0.2：召回 / TRACKER → Brief / Plan / Desk 详情
        const v302Route = resolvePushNotificationRoute(data);
        if (v302Route) {
          trackPushNotificationClick(data);
          if (!v302Route.startsWith(ROUTER_PATH.BRIEF)) {
            setPushEntryMarker(
              inferPushEntryDomain({
                domain:
                  typeof data.domain === "string" ? data.domain : undefined,
                routePath: v302Route,
                pushType,
              }),
            );
          }
          router.push(v302Route);
          return;
        }

        switch (pushType) {
          case "fcm.title.opendata.hottheme": {
            const path = getPath(data.market ?? "", data.category ?? "");
            if (path && data.slug) {
              router.push(`${ROUTER_PATH.MARKET}/${path}/${data.slug}`);
            }
            break;
          }
          case "fcm.title.mixture.ai_pick_publish":
            router.push(ROUTER_PATH.ALPHA_PICK_CURRENT_PICKS);
            break;
          case "fcm.title.mixture.ai_pick_deep_analysis":
            if (memberInfo?.isFree) {
              router.push(ROUTER_PATH.ALPHA_PICK_CURRENT_PICKS);
            } else if (data.link) {
              router.push(`${ROUTER_PATH.ALPHA_PICK_PICKS}/${data.link}`);
            }
            break;
          case "fcm.title.mixture.ai_pick_price_increase":
            if (data.link) {
              router.push(`${ROUTER_PATH.ALPHA_PICK_PICKS}/${data.link}`);
            }
            break;
          case "fcm.title.mixture.ai_pick_related_news":
            const path = getPath(data.market ?? "", data.category ?? "");
            router.push(`${ROUTER_PATH.NEWS}/${path}/${data.link}`);
            break;
        }
      },
    );
    return unsubscribe;
  }, [isInApp, memberInfo?.isFree, on, requestOpenRetainModalFromPush, router]);

  // deeplink 路由：原生只上报“解析后的最终 URL”，参数映射和跳转由 Web 接管
  const lastHandledDeepLinkKeyRef = useRef<string | null>(null);
  useEffect(() => {
    if (!isInApp || !on || !router) {
      return;
    }

    let cancelled = false;
    let deepLinkConsumed = false;

    const handleDeepLinkPayload = (
      data: DeepLinkOpenedData,
      via: "push" | "pull",
    ) => {
      if (!data?.url && !extractResultId(data)) {
        return;
      }
      log.info("[DEEPLINK] consume payload", {
        via,
        resultId: extractResultId(data),
        source: data?.source,
        platform: data?.platform,
      });
      handleDeepLinkOpened(data, via, {
        router,
        lastHandledDeepLinkKeyRef,
      });
      deepLinkConsumed = true;
    };

    const pullLastDeepLink = (delayMs: number) => {
      if (cancelled || deepLinkConsumed) {
        return Promise.resolve();
      }
      log.info("[DEEPLINK] pull attempt", { delayMs });
      return getBridge()
        .getLastDeepLink({ timeoutMs: 8000 })
        .then((data) => {
          if (cancelled || deepLinkConsumed) {
            return;
          }
          const resultId = extractResultId(data);
          if (data?.url || resultId) {
            handleDeepLinkPayload(data, "pull");
            return;
          }
          log.info("[DEEPLINK] pull empty", { delayMs });
        })
        .catch((error) => {
          log.info("[DEEPLINK] pull unavailable", {
            delayMs,
            error: error instanceof Error ? error.message : String(error),
          });
        });
    };

    const unsubscribe = on(
      MessageType.DEEP_LINK_OPENED,
      (data: DeepLinkOpenedData) => {
        log.info("[DEEPLINK] push received", {
          resultId: extractResultId(data),
          source: data?.source,
        });
        handleDeepLinkPayload(data, "push");
      },
    );

    const pullDelaysMs = getDeferredDeepLinkPullDelaysMs(
      getBridge().getPlatform(),
    );
    log.info("[DEEPLINK] pull schedule started", {
      platform: getBridge().getPlatform(),
      delaysMs: pullDelaysMs,
    });
    const pullTimers = pullDelaysMs.map((delayMs) =>
      setTimeout(() => {
        void pullLastDeepLink(delayMs);
      }, delayMs),
    );

    return () => {
      cancelled = true;
      for (const timer of pullTimers) {
        clearTimeout(timer);
      }
      unsubscribe();
    };
  }, [isInApp, on, router]);
}
